import { useEffect } from "react";

const SITE = "https://breakthetest.app";

/**
 * The canonical URL for a route: the form Cloudflare Pages actually serves at
 * 200, never the one that redirects to it.
 *
 * THE BLOG KEEPS ITS TRAILING SLASH AND EVERYTHING ELSE DROPS ONE. That is not a
 * style choice, it is what the server does, and the two families genuinely
 * differ:
 *
 *   /blog/a-post/   -> 200      /blog/a-post   -> 308 to the slash form
 *   /patterns       -> 200      /patterns/     -> 308 to the non-slash form
 *
 * `scripts/prerender.mjs` already encodes this: it writes a blog route to
 * `blog/<slug>/index.html` and a flat route to `<name>.html`, precisely so the
 * landing URL matches the sitemap with no redirect in between. The sitemap
 * agrees too.
 *
 * This function used to strip EVERY trailing slash, so every blog post told
 * Google its canonical was a URL that 308s away. Found 2026-10-06 in the
 * September Search Console export, where eight posts appear under BOTH forms and
 * their impressions are split between them: national-merit-cutoffs-2027 carried
 * 10 impressions on one and 1 on the other. The sitemap and the server were
 * already right; the canonical tag was the only thing disagreeing with them.
 */
export function canonicalPath(pathname: string): string {
  const path = pathname.split(/[?#]/)[0] || "/";
  if (path === "/") return "/";
  // Blog routes are SERVED with the slash, so that is the canonical form, no
  // matter which way the visitor arrived.
  if (path === "/blog" || path === "/blog/") return "/blog/";
  if (path.startsWith("/blog/")) return `/${path.replace(/^\/+|\/+$/g, "")}/`;
  return `/${path.replace(/^\/+|\/+$/g, "")}`;
}

/**
 * Keeps a single <link rel="canonical"> in sync with the current route, always
 * on the canonical domain. This is the per-route fix for a client-rendered SPA:
 * a static canonical in index.html would point every page at "/", dropping
 * /faq, /privacy, etc. from the index. Updating it per navigation makes each
 * page self-canonical and consolidates every duplicate access path
 * (http://, www, the *.pages.dev preview domain) to one indexable URL —
 * addresses GSC "Page with redirect" / duplicate-content. Googlebot renders JS,
 * so it sees the updated tag, and `prerender.mjs` snapshots it into the static
 * HTML for crawlers that do not.
 */
export function CanonicalLink({ pathname }: { pathname: string }) {
  useEffect(() => {
    const href = `${SITE}${canonicalPath(pathname)}`;
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = href;
  }, [pathname]);
  return null;
}
